/**
 * useNotificationCenter — live counts for the global nav.
 *
 * Subscribes the authenticated user to the singleton WebSocket and keeps two
 * counters in sync:
 *   - unreadMessages       — bumps on each inbound DIRECT_MESSAGE WS event
 *   - unreadNotifications  — bumps on connection requests, follows, group
 *                            invites, etc; persists via /api/notifications.
 *
 * Initial counts are fetched on mount via the REST endpoints so reloads stay
 * accurate; subsequent updates come from WS and are pure in-memory.
 *
 * Consumers (NavMessagesBadge, NotificationBell, etc.) read the counts and
 * mutators from this hook; toasts are fired centrally from here so we don't
 * double-toast when multiple components mount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '../context/AuthContext';
import { useWebSocket, WSMessageType } from './useWebSocket';
import { messageAPI, notificationAPI, userPreferencesAPI } from '../lib/api';

export const NOTIFICATION_CATEGORIES = [
  { key: 'direct_message', label: 'Direct messages', defaultEnabled: true },
  { key: 'new_follower', label: 'New followers', defaultEnabled: true },
  { key: 'connection_request', label: 'Connection requests', defaultEnabled: true },
  { key: 'connection_accepted', label: 'Connections accepted', defaultEnabled: true },
  { key: 'notification', label: 'System notifications', defaultEnabled: true },
];

const STORAGE_KEY = 'ti.notif.mute-preferences.v1';
const DND_STORAGE_KEY = 'ti.notif.dnd.v1';

// Available durations for the master Do Not Disturb switch (hours).
export const DND_DURATIONS = [
  { hours: 1, label: '1 hour' },
  { hours: 4, label: '4 hours' },
  { hours: 8, label: '8 hours' },
  { hours: 12, label: 'Until tomorrow (12h)' },
  { hours: 24, label: '24 hours' },
];

// Mute preferences are intentionally stored in sessionStorage rather than
// localStorage — the server is the source of truth (synced via
// `/api/user/preferences`) and the cache only avoids a brief toast flash on
// fresh page loads. Scoping to sessionStorage keeps XSS-exfil surface area
// minimal (Code Quality audit, iter 46 follow-up).
function _readPrefs() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function _writePrefs(prefs) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (err) {
    // sessionStorage can throw in private-browsing mode or when quota is
    // exceeded. Server is authoritative — local cache failure only costs
    // a brief toast-flash on next mount.
    console.debug('[notifications] mute-prefs write failed:', err?.message);
  }
}

// DnD intentionally stays in localStorage — it carries its own `until_iso`
// expiry and users expect "24h DnD" to survive a browser restart.
function _readDnd() {
  try {
    const raw = localStorage.getItem(DND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.until_iso) return null;
    if (new Date(parsed.until_iso).getTime() <= Date.now()) return null; // expired
    return parsed;
  } catch (_) {
    return null;
  }
}

function _writeDnd(value) {
  try {
    if (value) localStorage.setItem(DND_STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(DND_STORAGE_KEY);
  } catch (err) {
    // localStorage failure in private-browsing — DnD just won't persist
    // across reloads. Acceptable degradation.
    console.debug('[notifications] dnd write failed:', err?.message);
  }
}

export const useNotificationCenter = () => {
  const { isAuthenticated, user } = useAuth();
  const userId = isAuthenticated ? user?.user_id : null;

  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [mutePreferences, setMutePreferences] = useState(_readPrefs());
  const [dnd, setDnd] = useState(_readDnd());
  // Tracks whether the most recent server PUT for preferences succeeded.
  // null = no sync attempted yet, true = OK, false = failed.
  const [lastSyncOk, setLastSyncOk] = useState(null);

  // Auto-expire DnD when its `until_iso` passes. Re-runs only when dnd changes.
  useEffect(() => {
    if (!dnd?.until_iso) return undefined;
    const remaining = new Date(dnd.until_iso).getTime() - Date.now();
    if (remaining <= 0) {
      setDnd(null);
      _writeDnd(null);
      return undefined;
    }
    const t = setTimeout(() => {
      setDnd(null);
      _writeDnd(null);
    }, remaining);
    return () => clearTimeout(t);
  }, [dnd]);

  const isMuted = useCallback(
    (category) => {
      if (dnd?.until_iso && new Date(dnd.until_iso).getTime() > Date.now()) return true;
      if (mutePreferences[category] !== undefined) return mutePreferences[category] === false;
      // Fall back to category default (enabled).
      const cat = NOTIFICATION_CATEGORIES.find((c) => c.key === category);
      return cat ? !cat.defaultEnabled : false;
    },
    [mutePreferences, dnd],
  );

  const enableDnd = useCallback((hours) => {
    const until = new Date(
      Date.now() + Math.max(1, Number(hours) || 1) * 60 * 60 * 1000,
    ).toISOString();
    const next = { until_iso: until, hours };
    setDnd(next);
    _writeDnd(next);
  }, []);

  const disableDnd = useCallback(() => {
    setDnd(null);
    _writeDnd(null);
  }, []);

  // Sequential mutation queue: chains every server-bound PUT onto a single
  // promise so two rapid toggles can't race (the second always reads the
  // result of the first's write). Per iter-25 code-review note.
  const syncQueueRef = useRef(Promise.resolve());

  const _syncMutePrefsToServer = useCallback((nextPrefs) => {
    syncQueueRef.current = syncQueueRef.current
      .then(() => userPreferencesAPI.get())
      .then((r) => {
        const existing = r?.data?.preferences || {};
        return userPreferencesAPI.put({ ...existing, toast_mute: nextPrefs });
      })
      .then(() => {
        setLastSyncOk(true);
      })
      .catch((e) => {
        // Server failure doesn't roll back local state — user gets the
        // expected immediate UX, and next mount will reconcile. We surface
        // a subtle "Not synced" indicator via `lastSyncOk=false`.
        setLastSyncOk(false);

        console.warn('toast-mute preference server-sync failed:', e?.message);
        // Important: do NOT re-throw — keep the queue alive for future toggles.
      });
    return syncQueueRef.current;
  }, []);

  const toggleMute = useCallback(
    (category) => {
      let nextPrefs;
      setMutePreferences((prev) => {
        nextPrefs = {
          ...prev,
          [category]:
            prev[category] === undefined
              ? !(NOTIFICATION_CATEGORIES.find((c) => c.key === category)?.defaultEnabled ?? true)
              : !prev[category],
        };
        _writePrefs(nextPrefs);
        return nextPrefs;
      });
      // Best-effort sync to server — serialized via syncQueueRef so concurrent
      // toggles can't race. Failures surface via `lastSyncOk=false`.
      if (userId && nextPrefs) {
        _syncMutePrefsToServer(nextPrefs);
      }
    },
    [userId, _syncMutePrefsToServer],
  );

  // Initial fetch of persisted counters + server-side preferences.
  useEffect(() => {
    if (!userId) {
      setUnreadMessages(0);
      setUnreadNotifications(0);
      setNotifications([]);
      return;
    }
    let cancelled = false;

    Promise.all([
      messageAPI
        .getUnreadCount()
        .then((r) => r.data?.unread_count ?? 0)
        .catch(() => 0),
      notificationAPI
        .getUnreadCount()
        .then((r) => r.data?.unread_count ?? 0)
        .catch(() => 0),
      notificationAPI
        .getNotifications()
        .then((r) => r.data ?? [])
        .catch(() => []),
      userPreferencesAPI
        .get()
        .then((r) => r.data?.preferences?.toast_mute || null)
        .catch(() => null),
    ]).then(([m, n, list, serverMute]) => {
      if (cancelled) return;
      setUnreadMessages(m);
      setUnreadNotifications(n);
      setNotifications(list);
      if (serverMute && typeof serverMute === 'object') {
        // Server is authoritative across devices — adopt it locally.
        setMutePreferences(serverMute);
        _writePrefs(serverMute);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Inbound WS handler — bump counters + fire toasts (respecting mute prefs).
  const handleWS = useCallback(
    (msg) => {
      if (!msg?.type) return;
      const { type, data, sender_id } = msg;
      // Ignore our own outbound echo, if any.
      if (sender_id && sender_id === userId) return;

      switch (type) {
        case WSMessageType.DIRECT_MESSAGE: {
          setUnreadMessages((n) => n + 1);
          if (!isMuted('direct_message')) {
            const senderName = data?.sender_name || 'Someone';
            const preview = (data?.content || '').slice(0, 80);
            toast.message(`New message from ${senderName}`, {
              description: preview || undefined,
            });
          }
          break;
        }
        case WSMessageType.NEW_FOLLOWER: {
          setUnreadNotifications((n) => n + 1);
          const name = data?.follower_name || 'Someone';
          if (!isMuted('new_follower')) toast.success(`${name} followed you`);
          addLocalNotification('new_follower', `${name} followed you`);
          break;
        }
        case WSMessageType.CONNECTION_REQUEST: {
          setUnreadNotifications((n) => n + 1);
          const name = data?.requester_name || 'Someone';
          if (!isMuted('connection_request')) {
            toast.message(`Connection request from ${name}`, {
              description: data?.message || undefined,
            });
          }
          addLocalNotification('connection_request', `${name} sent a connection request`);
          break;
        }
        case WSMessageType.CONNECTION_ACCEPTED: {
          setUnreadNotifications((n) => n + 1);
          const name = data?.accepter_name || 'Someone';
          if (!isMuted('connection_accepted'))
            toast.success(`${name} accepted your connection request`);
          addLocalNotification('connection_accepted', `${name} accepted your connection`);
          break;
        }
        case WSMessageType.NOTIFICATION: {
          setUnreadNotifications((n) => n + 1);
          const title = data?.title || 'New notification';
          if (!isMuted('notification')) toast.message(title, { description: data?.message });
          addLocalNotification(data?.notification_type || 'system', title);
          break;
        }
        default:
          // Unhandled WS message types are ignored at this layer; consumer
          // components (e.g. GroupChat) handle them via their own callback.
          break;
      }
    },
    [userId, isMuted],
  );

  const addLocalNotification = (notification_type, message) => {
    setNotifications((prev) =>
      [
        {
          notification_id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          type: notification_type,
          title: message,
          message,
          is_read: false,
          created_at: new Date().toISOString(),
        },
        ...prev,
      ].slice(0, 50),
    );
  };

  // Attach to the singleton WS only when logged in.
  const { isConnected } = useWebSocket(userId, handleWS);

  // Auto-retry the mute preference sync the moment the WS transitions from
  // disconnected to connected, but only if we know a previous sync failed.
  // Piggybacks on the existing connection-status signal — no new endpoints,
  // no setInterval polling. Per iter-27 backlog item.
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!isConnected || wasConnected) return; // only fire on false → true
    if (!userId) return;
    if (lastSyncOk === false) {
      _syncMutePrefsToServer(mutePreferences);
    }
  }, [isConnected, userId, lastSyncOk, mutePreferences, _syncMutePrefsToServer]);

  // P3 — keep the browser tab title in sync with the unread count so users
  // notice new activity even when this tab isn't focused.
  useEffect(() => {
    const baseTitle = 'TI Networking';
    const total = unreadMessages + unreadNotifications;
    if (typeof document === 'undefined') return undefined;

    const original = document.title;
    document.title = total > 0 ? `(${total > 99 ? '99+' : total}) ${baseTitle}` : baseTitle;

    return () => {
      // Avoid leaving a stale `(N)` prefix on unmount.
      document.title = original.startsWith('(') ? baseTitle : original;
    };
  }, [unreadMessages, unreadNotifications]);

  const clearUnreadMessages = useCallback(async () => {
    setUnreadMessages(0);
    try {
      await messageAPI.markAllRead();
    } catch (e) {
      // Best-effort; UI already updated. Server will reconcile on next mount.
      console.debug('clearUnreadMessages failed:', e?.message);
    }
  }, []);

  const clearThreadUnread = useCallback(async (otherUserId) => {
    // Locally decrement is hard without knowing how many were unread for this
    // thread, so we just re-fetch the canonical count after the server update.
    try {
      await messageAPI.markThreadRead(otherUserId);
      const fresh = await messageAPI.getUnreadCount();
      setUnreadMessages(fresh.data?.unread_count ?? 0);
    } catch (e) {
      // Network failure — leave the count alone, will reconcile on reload.
      console.debug('clearThreadUnread failed:', e?.message);
    }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    setUnreadNotifications(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await notificationAPI.markAllAsRead();
    } catch (e) {
      // Best-effort; UI already updated. Server will catch up on next mount.
      console.debug('markAllNotificationsRead failed:', e?.message);
    }
  }, []);

  return {
    unreadMessages,
    unreadNotifications,
    notifications,
    clearUnreadMessages,
    clearThreadUnread,
    markAllNotificationsRead,
    mutePreferences,
    toggleMute,
    dnd,
    enableDnd,
    disableDnd,
    lastSyncOk,
  };
};

export default useNotificationCenter;
